import React, { useState } from "react";
import { motion } from "framer-motion";
import { pickLang } from "../../translations";
import { parseAllergens } from "./allergens";

const ProductSheet = ({ product, onClose, lang, t, formatPrice }) => {
  const name = pickLang(product, lang, "name");
  const desc = pickLang(product, lang, "longDescription") || pickLang(product, lang, "shortDescription");
  const ing = pickLang(product, lang, "ingredients");
  const allergenText = pickLang(product, lang, "allergens");
  const allergens = parseAllergens(allergenText, lang);

  // Build gallery: main image + extra gallery images
  const images = [product.image, ...(product.gallery || [])].filter(Boolean);
  const [imgIdx, setImgIdx] = useState(0);
  const prev = () => setImgIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setImgIdx((i) => (i + 1) % images.length);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="product-sheet"
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-xl bg-zbeach-bg sm:rounded-3xl rounded-t-3xl max-h-[100dvh] sm:max-h-[92vh] overflow-y-auto flex flex-col"
      >
        {/* Sticky close button */}
        <div className="sticky top-0 z-20 bg-zbeach-bg/95 backdrop-blur-md flex items-center justify-between px-4 py-2 border-b border-zbeach-gold/10">
          <div className="w-12 h-1.5 bg-zbeach-muted/30 rounded-full mx-auto absolute left-1/2 -translate-x-1/2 top-1" />
          <span className="w-9 h-9" />
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-zbeach-surface border border-zbeach-gold/20 flex items-center justify-center text-zbeach-muted hover:text-zbeach-dark mt-2" data-testid="sheet-close" aria-label="Kapat">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Big image with optional carousel */}
        {images.length > 0 && (
          <div className="relative px-4 mt-3">
            <div className="rounded-2xl overflow-hidden aspect-[5/4] bg-zbeach-sand">
              <img src={images[imgIdx]} alt={name} className="w-full h-full object-cover" data-testid="sheet-image" />
            </div>
            {images.length > 1 && (
              <>
                <button onClick={prev} className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-md flex items-center justify-center text-zbeach-dark shadow-md" aria-label="Önceki">
                  <i className="fa-solid fa-chevron-left" />
                </button>
                <button onClick={next} className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-md flex items-center justify-center text-zbeach-dark shadow-md" aria-label="Sonraki">
                  <i className="fa-solid fa-chevron-right" />
                </button>
              </>
            )}
          </div>
        )}

        <div className="px-6 mt-5 flex-1">
          <h2 className="font-serif text-2xl sm:text-3xl text-zbeach-dark font-semibold leading-tight" data-testid="sheet-title">{name}</h2>
          {product.portionInfo && (
            <p className="text-xs text-zbeach-muted mt-1">{product.portionInfo}</p>
          )}
          {desc && <p className="text-zbeach-muted text-sm mt-3 leading-relaxed">{desc}</p>}

          {/* Tag pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            {product.isPopular && <Tag color="bg-zbeach-gold/20 text-zbeach-gold">{t.popular}</Tag>}
            {product.isNew && <Tag color="bg-zbeach-turquoise/15 text-zbeach-turquoise">{t.new}</Tag>}
            {product.isChefRecommended && <Tag color="bg-zbeach-dark/10 text-zbeach-dark">{t.chef}</Tag>}
            {product.isVegan && <Tag color="bg-green-100 text-green-700">{t.vegan}</Tag>}
            {product.isVegetarian && <Tag color="bg-emerald-100 text-emerald-700">{t.vegetarian}</Tag>}
            {product.isGlutenFree && <Tag color="bg-amber-100 text-amber-700">{t.glutenFree}</Tag>}
            {product.isSpicy && <Tag color="bg-red-100 text-red-700">{t.spicy}</Tag>}
          </div>

          {/* Allergen icon row (Wyndham-style) */}
          {allergens.length > 0 && (
            <div className="mt-6 border-y border-zbeach-gold/20 py-4">
              <div className="grid grid-cols-4 gap-3">
                {allergens.map((a, i) => (
                  <div key={i} className="flex flex-col items-center text-center" data-testid={`allergen-${i}`}>
                    <i className={`fa-solid ${a.icon} text-2xl text-zbeach-dark/70`} />
                    <div className="text-[10px] mt-1.5 text-zbeach-muted leading-tight">{a.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ing && (
            <div className="mt-6">
              <h3 className="font-serif text-base text-zbeach-dark uppercase tracking-wider">{t.ingredients}</h3>
              <p className="text-zbeach-muted text-sm mt-1.5">{ing}</p>
            </div>
          )}

          {product.variants?.filter((v) => v.isActive).length > 0 && (
            <div className="mt-6">
              <h3 className="font-serif text-base text-zbeach-dark uppercase tracking-wider mb-2">{lang === "en" ? "Options" : "Seçenekler"}</h3>
              <div className="space-y-2">
                {product.variants.filter((v) => v.isActive).map((v) => (
                  <div key={v.id} className="flex justify-between items-center bg-zbeach-surface border border-zbeach-gold/15 rounded-xl px-4 py-2.5">
                    <span className="text-sm">{pickLang(v, lang, "name")}</span>
                    {v.extraPrice > 0 && <span className="text-sm font-medium text-zbeach-turquoise">+{formatPrice(v.extraPrice)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.extras?.filter((e) => e.isActive).length > 0 && (
            <div className="mt-6">
              <h3 className="font-serif text-base text-zbeach-dark uppercase tracking-wider mb-2">{lang === "en" ? "Extras" : "Ekstralar"}</h3>
              <div className="space-y-2">
                {product.extras.filter((e) => e.isActive).map((e) => (
                  <div key={e.id} className="flex justify-between items-center bg-zbeach-surface border border-zbeach-gold/15 rounded-xl px-4 py-2.5">
                    <span className="text-sm">{pickLang(e, lang, "name")}</span>
                    {e.extraPrice > 0 && <span className="text-sm font-medium text-zbeach-turquoise">+{formatPrice(e.extraPrice)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Price box (Wyndham-style sticky bottom) */}
        <div className="px-6 mt-6 pb-6">
          <div className="bg-zbeach-sand/50 border border-zbeach-gold/20 rounded-2xl py-4 text-center" data-testid="sheet-price-box">
            <div className="text-xs uppercase tracking-widest text-zbeach-muted">{name}</div>
            {product.discountedPrice ? (
              <div className="flex items-baseline justify-center gap-3 mt-1">
                <span className="font-serif text-3xl font-semibold text-zbeach-turquoise" data-testid="sheet-price">{formatPrice(product.discountedPrice)}</span>
                <span className="text-zbeach-muted line-through text-sm">{formatPrice(product.price)}</span>
              </div>
            ) : (
              <div className="font-serif text-3xl font-semibold text-zbeach-turquoise mt-1" data-testid="sheet-price">{formatPrice(product.price)}</div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Tag = ({ color, children }) => <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${color}`}>{children}</span>;

export default ProductSheet;
